import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import useApiStable from '../utils/useApiStable';
import WorkDutyAccountCard from './workduty/WorkDutyAccountCard';
import '../styles/TaskList.css';

const GENDER_LABELS = { MALE: 'Männlich', FEMALE: 'Weiblich', NOT_SELECTED: 'Keine Angabe' };

const Profile = () => {
  const [person, setPerson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Profil bearbeiten
  const [editing, setEditing] = useState(false);
  const [editGender, setEditGender] = useState('');
  const [editIdNumber, setEditIdNumber] = useState('');
  const [editTennis04Id, setEditTennis04Id] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  // TNB-Spielersuche
  const [tnbPlayers, setTnbPlayers] = useState([]);
  const [tnbFilter, setTnbFilter] = useState('');

  // Tennis04-Spielersuche
  const [tennis04SearchTerm, setTennis04SearchTerm] = useState('');
  const [tennis04SearchResults, setTennis04SearchResults] = useState([]);
  const [tennis04Searching, setTennis04Searching] = useState(false);

  // Passwort ändern
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  // Datenschutz
  const [matchesPublic, setMatchesPublic] = useState(true);
  const [privacyLoading, setPrivacyLoading] = useState(false);
  const [privacySuccess, setPrivacySuccess] = useState('');
  const [privacyError, setPrivacyError] = useState('');

  // Benachrichtigungen (Standard: aktiviert mit Filter "ALLE")
  const [notifEnabled, setNotifEnabled] = useState(true);
  const [notifFilter, setNotifFilter] = useState('ALLE');
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifSuccess, setNotifSuccess] = useState('');
  const [notifError, setNotifError] = useState('');

  const { user } = useAuth();
  const api = useApiStable();

  const loadPerson = () => {
    if (user?.username) {
      setLoading(true);
      api.get('/persons/me')
        .then(person => {
          if (person) {
            setPerson(person);
          } else {
            setError('Person nicht gefunden');
          }
          setLoading(false);
        })
        .catch(err => {
          setError('Fehler beim Laden der Daten: ' + err.message);
          setLoading(false);
        });
    }
  };

  const loadPrivacySettings = () => {
    api.get('/persons/me/privacy-settings')
      .then(settings => setMatchesPublic(settings.matchesPublic))
      .catch(() => {});
  };

  const handleSavePrivacySettings = async () => {
    setPrivacyLoading(true);
    setPrivacySuccess('');
    setPrivacyError('');
    try {
      const result = await api.put('/persons/me/privacy-settings', { matchesPublic });
      setMatchesPublic(result.matchesPublic);
      setPrivacySuccess('Einstellungen gespeichert.');
      setTimeout(() => setPrivacySuccess(''), 3000);
    } catch (err) {
      setPrivacyError(err.message || 'Speichern fehlgeschlagen');
    } finally {
      setPrivacyLoading(false);
    }
  };

  const loadNotificationPreferences = () => {
    api.get('/persons/me/notification-preferences')
      .then(pref => {
        setNotifEnabled(pref.notificationsEnabled);
        setNotifFilter(pref.bockFilter);
      })
      .catch(() => {});
  };

  const handleSaveNotificationPreferences = async () => {
    setNotifLoading(true);
    setNotifSuccess('');
    setNotifError('');
    try {
      const pref = await api.put('/persons/me/notification-preferences', {
        notificationsEnabled: notifEnabled,
        bockFilter: notifFilter,
      });
      setNotifEnabled(pref.notificationsEnabled);
      setNotifFilter(pref.bockFilter);
      setNotifSuccess('Einstellungen gespeichert.');
      setTimeout(() => setNotifSuccess(''), 3000);
    } catch (err) {
      setNotifError(err.message || 'Speichern fehlgeschlagen');
    } finally {
      setNotifLoading(false);
    }
  };

  useEffect(() => {
    loadPerson();
    loadPrivacySettings();
    loadNotificationPreferences();
  }, [user]);

  const handleStartEdit = () => {
    setEditGender(person.gender || '');
    setEditIdNumber(person.idNumber || '');
    setEditTennis04Id(person.tennis04Id != null ? String(person.tennis04Id) : '');
    setSaveError('');
    setSaveSuccess('');
    setTnbFilter('');
    setTennis04SearchTerm('');
    setTennis04SearchResults([]);
    setEditing(true);
    api.get('/league/teams/licensed-players')
      .then(players => setTnbPlayers(players))
      .catch(() => setTnbPlayers([]));
  };

  const handleTennis04Search = async () => {
    if (!tennis04SearchTerm.trim()) return;
    setTennis04Searching(true);
    try {
      const results = await api.get(`/tennis04/player-search?term=${encodeURIComponent(tennis04SearchTerm)}`);
      setTennis04SearchResults(Array.isArray(results) ? results : []);
    } catch {
      setTennis04SearchResults([]);
    } finally {
      setTennis04Searching(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaveError('');
    setSaveSuccess('');
    try {
      await api.put(`/persons/${person.id}`, {
        firstName: person.firstName,
        lastName: person.lastName,
        email: person.email,
        tennis04Id: editTennis04Id !== '' ? parseInt(editTennis04Id, 10) : null,
        gender: editGender || null,
        idNumber: editIdNumber.trim() || null,
      });
      setSaveSuccess('Profil gespeichert.');
      setEditing(false);
      loadPerson();
      setTimeout(() => setSaveSuccess(''), 3000);
    } catch (err) {
      setSaveError(err.message || 'Speichern fehlgeschlagen');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!person?.id) {
      setPasswordError('Person-ID nicht gefunden');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Das neue Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Die Passwörter stimmen nicht überein.');
      return;
    }

    try {
      const response = await api.fetchRaw(`/persons/${person.id}/change-password`, {
        method: 'POST',
        body: JSON.stringify({ oldPassword, newPassword })
      });

      const result = await response.json();

      if (response.ok) {
        setPasswordSuccess('Passwort erfolgreich geändert!');
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => setPasswordSuccess(''), 3000);
      } else {
        setPasswordError(result.error || 'Passwortänderung fehlgeschlagen');
      }
    } catch (err) {
      setPasswordError(err.message || 'Passwortänderung fehlgeschlagen');
    }
  };

  const handleCopyCalendarUrl = () => {
    if (!person?.uuid) return;
    const calendarUrl = `${window.location.origin}/api/calendar/ical/person/${person.uuid}`;
    navigator.clipboard.writeText(calendarUrl).then(() => {
      alert(`Kalender-URL wurde in die Zwischenablage kopiert!\n\nFür iPhone:\n1. Öffne die Kalender-App\n2. Tippe auf "Kalender" unten\n3. Tippe auf "Kalender hinzufügen"\n4. Wähle "Kalenderabo hinzufügen"\n5. Füge die kopierte URL ein\n\nURL: ${calendarUrl}`);
    }).catch(() => {
      prompt('Kopiere diese URL für deinen Kalender:', calendarUrl);
    });
  };

  const openCalendarUrl = () => {
    if (!person?.uuid) return;
    window.open(`${window.location.origin}/api/calendar/ical/person/${person.uuid}`, '_blank');
  };

  if (loading) {
    return (
      <div className="container" style={{ maxWidth: '800px', margin: '50px auto', padding: '0 20px' }}>
        <div className="taskform-card"><p>Lädt...</p></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container" style={{ maxWidth: '800px', margin: '50px auto', padding: '0 20px' }}>
        <div className="taskform-card"><p>Bitte melden Sie sich an, um Ihr Profil zu sehen.</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ maxWidth: '800px', margin: '50px auto', padding: '0 20px' }}>
        <div className="taskform-card"><p style={{ color: '#c62828' }}>{error}</p></div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '800px', margin: '50px auto', padding: '0 20px' }}>
      <div className="taskform-card">
        <h2 style={{ marginBottom: '30px' }}>Mein Profil</h2>

        {/* Persönliche Informationen */}
        <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #ddd' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ fontSize: '1.2em', color: '#333', margin: 0 }}>Persönliche Informationen</h3>
            {!editing && (
              <button onClick={handleStartEdit} className="tasklist-btn" style={{ background: '#1976D2' }}>
                Profil bearbeiten
              </button>
            )}
          </div>

          {saveSuccess && (
            <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '10px 14px', borderRadius: '4px', marginBottom: '12px', fontSize: '0.9em' }}>
              {saveSuccess}
            </div>
          )}

          {!editing ? (
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <tbody>
                {[
                  ['Name', person?.name],
                  ['E-Mail', person?.email],
                  ['Geschlecht', GENDER_LABELS[person?.gender] || null],
                  ['ID-Nummer', person?.idNumber],
                  ['Tennis04-ID', person?.tennis04Id != null ? String(person.tennis04Id) : null],
                  ['Registriert am', person?.createdAt ? new Date(person.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null],
                ].map(([label, value]) => (
                  <tr key={label} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px 16px 8px 0', color: '#666', whiteSpace: 'nowrap', width: '1%' }}>{label}</td>
                    <td style={{ padding: '8px 0', color: value ? '#111' : '#bbb', fontStyle: value ? 'normal' : 'italic' }}>
                      {value || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div>
              {saveError && (
                <div style={{ background: '#ffebee', color: '#c62828', padding: '10px 14px', borderRadius: '4px', marginBottom: '12px', fontSize: '0.9em' }}>
                  {saveError}
                </div>
              )}
              <div className="taskform-group">
                <label>Geschlecht</label>
                <select value={editGender} onChange={e => setEditGender(e.target.value)} style={{ width: '100%' }}>
                  <option value="">— nicht ausgewählt —</option>
                  <option value="MALE">Männlich</option>
                  <option value="FEMALE">Weiblich</option>
                  <option value="NOT_SELECTED">Keine Angabe</option>
                </select>
              </div>
              <div className="taskform-group">
                <label>TNB-Lizenznummer</label>
                <input
                  type="text"
                  placeholder="Nach Name filtern…"
                  value={tnbFilter}
                  onChange={e => setTnbFilter(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', marginBottom: '4px' }}
                />
                <select
                  value={editIdNumber}
                  onChange={e => setEditIdNumber(e.target.value)}
                  style={{ width: '100%', height: '120px' }}
                  size={5}
                >
                  <option value="">— keine Auswahl —</option>
                  {tnbPlayers
                    .filter(p => tnbFilter === '' ||
                      p.name.toLowerCase().includes(tnbFilter.toLowerCase()) ||
                      (p.licenseNumber || '').includes(tnbFilter))
                    .map(p => (
                      <option key={p.licenseNumber} value={p.licenseNumber}>
                        {p.name} ({p.licenseNumber}){p.lk ? ` · LK ${p.lk}` : ''}
                      </option>
                    ))
                  }
                </select>
                {tnbPlayers.length === 0 && (
                  <span style={{ fontSize: '0.8em', color: '#999' }}>
                    Keine TNB-Spieler verfügbar (ggf. erst synchronisieren)
                  </span>
                )}
                {editIdNumber && (
                  <div style={{ fontSize: '0.85em', color: '#555', marginTop: '4px' }}>
                    Ausgewählt: <strong>{editIdNumber}</strong>
                    <button
                      type="button"
                      onClick={() => setEditIdNumber('')}
                      style={{ marginLeft: '8px', fontSize: '0.8em', cursor: 'pointer', border: 'none', background: 'none', color: '#c62828' }}
                    >✕ Auswahl aufheben</button>
                  </div>
                )}
              </div>
              <div className="taskform-group">
                <label>Tennis04-ID</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                  <input
                    type="text"
                    placeholder="Nach Name suchen…"
                    value={tennis04SearchTerm}
                    onChange={e => setTennis04SearchTerm(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleTennis04Search()}
                    style={{ flex: 1, boxSizing: 'border-box' }}
                  />
                  <button
                    type="button"
                    onClick={handleTennis04Search}
                    disabled={tennis04Searching}
                    className="tasklist-btn"
                    style={{ background: '#1976D2', whiteSpace: 'nowrap' }}
                  >
                    {tennis04Searching ? '…' : 'Suchen'}
                  </button>
                </div>
                {tennis04SearchResults.length > 0 && (
                  <select
                    size={Math.min(5, tennis04SearchResults.length)}
                    onChange={e => setEditTennis04Id(e.target.value)}
                    style={{ width: '100%', marginBottom: '4px' }}
                    defaultValue=""
                  >
                    <option value="" disabled>— Spieler auswählen —</option>
                    {tennis04SearchResults.map(p => (
                      <option key={p.idWithinOrganisation} value={p.idWithinOrganisation}>
                        {p.firstName} {p.lastName} (ID: {p.idWithinOrganisation})
                      </option>
                    ))}
                  </select>
                )}
                {tennis04SearchResults.length === 0 && tennis04SearchTerm && !tennis04Searching && (
                  <div style={{ fontSize: '0.85em', color: '#999', marginBottom: '4px' }}>Keine Ergebnisse</div>
                )}
                <input
                  type="number"
                  value={editTennis04Id}
                  onChange={e => setEditTennis04Id(e.target.value)}
                  min="0"
                  step="1"
                  placeholder="Tennis04-ID (Zahl)"
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button onClick={handleSaveProfile} className="tasklist-btn" style={{ background: '#4CAF50' }}>
                  Speichern
                </button>
                <button onClick={() => setEditing(false)} className="tasklist-btn" style={{ background: '#757575' }}>
                  Abbrechen
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Gruppen-Mitgliedschaften */}
        <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #ddd' }}>
          <h3 style={{ fontSize: '1.2em', marginBottom: '15px', color: '#333' }}>Meine Gruppen</h3>
          {person?.groups && person.groups.length > 0 ? (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {person.groups.map(group => (
                <li key={group.id} style={{
                  background: '#f5f5f5',
                  padding: '10px 15px',
                  marginBottom: '8px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '1.2em' }}>👥</span>
                  <div>
                    <strong>{group.name}</strong>
                    {group.description && (
                      <div style={{ fontSize: '0.9em', color: '#666', marginTop: '2px' }}>{group.description}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ color: '#666', fontStyle: 'italic' }}>Sie sind noch in keiner Gruppe.</p>
          )}
        </div>

        {/* Kalender */}
        <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #ddd' }}>
          <h3 style={{ fontSize: '1.2em', marginBottom: '15px', color: '#333' }}>Mein Kalender</h3>
          <p style={{ marginBottom: '15px', color: '#666', fontSize: '0.9em' }}>
            Abonnieren Sie Ihren persönlichen Kalender, um alle Ihre Termine automatisch in Ihrer Kalender-App zu sehen.
          </p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button onClick={handleCopyCalendarUrl} className="tasklist-btn" style={{ background: '#4CAF50', display: 'flex', alignItems: 'center', gap: '8px' }}>
              📋 Kalender-URL kopieren
            </button>
            <button onClick={openCalendarUrl} className="tasklist-btn" style={{ background: '#2196F3', display: 'flex', alignItems: 'center', gap: '8px' }}>
              📅 Kalender öffnen
            </button>
          </div>
        </div>

        {/* Datenschutz */}
        <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #ddd' }}>
          <h3 style={{ fontSize: '1.2em', marginBottom: '15px', color: '#333' }}>Datenschutz</h3>
          {privacySuccess && (
            <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '10px 14px', borderRadius: '4px', marginBottom: '12px', fontSize: '0.9em' }}>
              {privacySuccess}
            </div>
          )}
          {privacyError && (
            <div style={{ background: '#ffebee', color: '#c62828', padding: '10px 14px', borderRadius: '4px', marginBottom: '12px', fontSize: '0.9em' }}>
              {privacyError}
            </div>
          )}
          <div className="taskform-group" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <label style={{ margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                checked={matchesPublic}
                onChange={e => setMatchesPublic(e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              Meine Begegnungen öffentlich anzeigen
            </label>
          </div>
          <p style={{ fontSize: '0.85em', color: '#666', marginBottom: '12px' }}>
            Wenn aktiviert, werden deine Spielpaarungen (Turnier und Liga) auf der Startseite und in öffentlichen Übersichten angezeigt.
          </p>
          <button
            onClick={handleSavePrivacySettings}
            disabled={privacyLoading}
            className="tasklist-btn"
            style={{ background: '#4CAF50' }}
          >
            {privacyLoading ? 'Wird gespeichert…' : 'Einstellungen speichern'}
          </button>
        </div>

        <WorkDutyAccountCard />

        {/* Benachrichtigungen */}
        <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #ddd' }}>
          <h3 style={{ fontSize: '1.2em', marginBottom: '15px', color: '#333' }}>Benachrichtigungen</h3>
          {notifSuccess && (
            <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '10px 14px', borderRadius: '4px', marginBottom: '12px', fontSize: '0.9em' }}>
              {notifSuccess}
            </div>
          )}
          {notifError && (
            <div style={{ background: '#ffebee', color: '#c62828', padding: '10px 14px', borderRadius: '4px', marginBottom: '12px', fontSize: '0.9em' }}>
              {notifError}
            </div>
          )}
          <p style={{ fontSize: '0.85em', color: '#666', marginBottom: '12px' }}>
            Push-Benachrichtigungen sind standardmäßig aktiviert. Du erhältst eine Meldung, wenn jemand einen Bock setzt. Du kannst das hier jederzeit ändern.
          </p>
          <div className="taskform-group" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
            <label style={{ margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                checked={notifEnabled}
                onChange={e => setNotifEnabled(e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              Push-Benachrichtigungen aktivieren
            </label>
          </div>
          {notifEnabled && (
            <div className="taskform-group">
              <label>Bock-Filter</label>
              <select
                value={notifFilter}
                onChange={e => setNotifFilter(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="ALLE">Alle</option>
                <option value="HERREN">Herren</option>
                <option value="DAMEN">Damen</option>
                <option value="KEINE">Keine</option>
              </select>
            </div>
          )}
          <button
            onClick={handleSaveNotificationPreferences}
            disabled={notifLoading}
            className="tasklist-btn"
            style={{ background: '#4CAF50', marginTop: '8px' }}
          >
            {notifLoading ? 'Wird gespeichert…' : 'Einstellungen speichern'}
          </button>
        </div>

        {/* Passwort ändern */}
        <div>
          <h3 style={{ fontSize: '1.2em', marginBottom: '15px', color: '#333' }}>Sicherheit</h3>
          <button
            type="button"
            onClick={() => setShowPasswordSection(!showPasswordSection)}
            className="tasklist-btn"
            style={{ background: '#FF9800', marginBottom: showPasswordSection ? '15px' : '0' }}
          >
            {showPasswordSection ? '🔒 Passwort-Bereich schließen' : '🔑 Passwort ändern'}
          </button>

          {showPasswordSection && (
            <div style={{ marginTop: '15px', padding: '20px', background: '#fff3e0', borderRadius: '8px' }}>
              {passwordError && (
                <div style={{ background: '#ffebee', color: '#c62828', padding: '12px', borderRadius: '4px', marginBottom: '15px', fontSize: '0.9em' }}>
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '12px', borderRadius: '4px', marginBottom: '15px', fontSize: '0.9em' }}>
                  ✓ {passwordSuccess}
                </div>
              )}
              <form onSubmit={handlePasswordChange}>
                <div className="taskform-group">
                  <label htmlFor="oldPassword">Altes Passwort*</label>
                  <input id="oldPassword" type="password" value={oldPassword} onChange={e => setOldPassword(e.target.value)} required style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
                <div className="taskform-group">
                  <label htmlFor="newPassword">Neues Passwort* (mind. 6 Zeichen)</label>
                  <input id="newPassword" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={6} style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
                <div className="taskform-group">
                  <label htmlFor="confirmPassword">Neues Passwort bestätigen*</label>
                  <input id="confirmPassword" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required minLength={6} style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
                <button type="submit" className="tasklist-btn" style={{ background: '#4CAF50', width: '100%' }}>
                  Passwort ändern
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
